'use client';

import { useSyncExternalStore } from 'react';

import { ArrowUp } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { useCookieAcknowledged } from '@/components/cookie-ack';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const SHOW_AT = 800;

let frame = 0;
const listeners = new Set<() => void>();

function subscribe(onChange: () => void): () => void {
  if (listeners.size === 0) {
    window.addEventListener('scroll', handleScroll, { passive: true });
  }
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) {
      window.removeEventListener('scroll', handleScroll);
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
    }
  };
}

function handleScroll() {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    listeners.forEach(l => l());
  });
}

// Read scroll position directly so the snapshot is always current; React's
// useSyncExternalStore compares with Object.is and only re-renders when the
// boolean flips, so per-pixel scroll changes don't cost anything.
function getSnapshot() {
  return window.scrollY > SHOW_AT;
}

function getServerSnapshot() {
  return false;
}

export function BackToTop() {
  const scrolledPast = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const cookieAcknowledged = useCookieAcknowledged();
  const reduceMotion = useReducedMotion();
  // Hide while the cookie banner is showing — on mobile it covers the same
  // bottom-right area, so the button would sit underneath it. The button's
  // z-index must stay below both the cookie banner (cookie-notice.tsx) and
  // the mobile menu overlay (header.tsx).
  const visible = scrolledPast && cookieAcknowledged;

  return (
    <AnimatePresence>
      {visible && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.button
                type="button"
                aria-label="Back to top"
                onClick={() =>
                  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
                }
                initial={reduceMotion ? false : { opacity: 0, y: 8, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.9 }}
                transition={{ duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' }}
                className="fixed right-4 bottom-4 z-30 flex size-10 cursor-pointer items-center justify-center rounded-full border border-border bg-background text-foreground shadow-lg transition-colors hover:bg-muted sm:right-6 sm:bottom-6"
              >
                <ArrowUp size={18} aria-hidden />
              </motion.button>
            </TooltipTrigger>
            <TooltipContent side="left" sideOffset={8}>
              Back to top
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </AnimatePresence>
  );
}
